/*
PURPOSE: Individual artist card component for coral artist grid
USE CASES:
- Display artist artwork, name, and key information
- Show visual feedback for hover interactions
- Provide consistent artist representation across corals
DOMAIN BOUNDARIES:
- Responsible for rendering a single artist within a coral
- Handles visual states and interactions
- Does not fetch or modify artist data
ASSUMPTIONS:
- Artist data includes name and potentially artwork
- Cards should be compact but informative
- Hover interactions should provide visual feedback
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientArtistListItem } from 'shared/types/platformTypes';

interface ArtistCardProps {
  artist: ClientArtistListItem;
  size?: 'sm' | 'md';
}

const ArtistCard: React.FC<ArtistCardProps> = ({ artist, size = 'md' }) => {
  const navigate = useNavigate();
  
  // Generate artist initials for fallback
  const getArtistInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // Generate consistent colors based on artist name
  const getArtistColors = (name: string) => {
    const colors = [
      { bg: 'bg-red-100', text: 'text-red-600' },
      { bg: 'bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-green-100', text: 'text-green-600' },
      { bg: 'bg-purple-100', text: 'text-purple-600' },
      { bg: 'bg-yellow-100', text: 'text-yellow-600' },
      { bg: 'bg-pink-100', text: 'text-pink-600' },
      { bg: 'bg-indigo-100', text: 'text-indigo-600' },
      { bg: 'bg-orange-100', text: 'text-orange-600' },
    ];
    
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const artistName = artist.artistName || 'Unknown Artist';
  const initials = getArtistInitials(artistName);
  const colors = getArtistColors(artistName);

  const handleArtistClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent coral card click
    if (artist.artistId) {
      navigate(`/artist/${artist.artistId}`);
    }
  };

  const sizeClasses = {
    sm: {
      text: 'text-lg',
      nameText: 'text-xs',
      padding: 'p-2'
    },
    md: {
      text: 'text-2xl', 
      nameText: 'text-xs',
      padding: 'p-3'
    }
  };

  return (
    <div className="group cursor-pointer" onClick={handleArtistClick}>
      <div className="aspect-square relative overflow-hidden rounded-lg bg-gray-100 hover:scale-105 transition-transform duration-200">
        {/* Artist image or fallback */}
        {artist.artistData?.imageUrl ? (
          <img
            src={artist.artistData.imageUrl}
            alt={artistName}
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              // Hide image and show fallback
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${colors.bg}`}>
            <span className={`${sizeClasses[size].text} font-bold ${colors.text}`}>
              {initials}
            </span>
          </div>
        )}
        
        {/* Overlay with artist name */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors duration-200 flex items-end">
          <div className={`${sizeClasses[size].padding} text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-full`}>
            <p className="text-sm font-medium truncate">
              {artistName}
            </p>
          </div>
        </div>
      </div>
      
      {/* Artist name below image */}
      <div className="mt-2 px-1">
        <p className={`${sizeClasses[size].nameText} text-gray-600 font-medium truncate text-center`}>
          {artistName}
        </p>
      </div>
    </div>
  );
};

export default ArtistCard;